import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import fetch from "cross-fetch";
import { getAuth } from "firebase/auth";
import { trace } from "firebase/performance";
import { v4 as uuidv4 } from "uuid";
import { analytics, perf } from "../Config/firebase";
import { BACKEND_URI } from "../Constants/constants";
import { setRequestId } from "../Redux/Slices/PaymentSlice";
import { store } from "../Redux/Store/Store";
import { apiLogEvents } from "./helper";

const isAdmin = process.env.REACT_APP_CXP_ADMIN_ACCESS_ENABLED;
// Request Interceptors
const authLink = setContext(async (_, operation) => {
  const { requestId } = operation;
  let authToken = await getAuth().currentUser?.getIdToken();
  const { userToken } = store.getState().userInfo;
  //@ts-ignore
  apiLogEvents(analytics, "req", _.operationName);

  const storedTokenData: any = localStorage.getItem("tokenResponse");
  const parsedData = JSON.parse(storedTokenData);
  if (storedTokenData) {
    const updatedData = {
      ...parsedData,
      idToken: authToken,
    };
    localStorage.setItem("tokenResponse", JSON.stringify(updatedData));
  }

  return {
    headers: {
      authorization: authToken ? `Bearer ${authToken}` : userToken ? `Bearer ${userToken}` : "",
      requestId: requestId,
    },
  };
});

const perfLink = new ApolloLink((operation, forward) => {
  const traceName = `${operation.operationName || "anonymous"}`;
  const t = trace(perf, traceName);

  t.start();

  const start = performance.now();

  return forward(operation).map((response) => {
    const end = performance.now();
    const duration = ((end - start) / 1000).toFixed(2);

    // Add custom metrics
    t.putMetric("duration_ms", parseFloat(duration));
    t.putAttribute("operationName", operation.operationName || "unknown");
    t.putAttribute("hasErrors", response.errors ? "true" : "false");

    t.stop();

    console.warn(`🔥 Firebase trace: ${traceName} took ${duration} s`);

    return response;
  });
});

// Custom Link for request and response handling
const requestResponseLink = new ApolloLink((operation, forward) => {
  const requestId = uuidv4(); // Use uuidv4 to generate a unique ID
  operation.setContext({ requestId });
  store.dispatch(setRequestId(requestId));
  if (operation.operationName === "getAddpaymentMethodURL") {
    sessionStorage.setItem("addPaymentMethodRequestId", requestId);
  }
  return forward(operation);
});

const responseLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    return response;
  });
});

// Initialize Apollo Client
export const client = new ApolloClient({
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError, response, operation, forward }) => {
      // Handle errors
      if (graphQLErrors) {
        const errors = {
          name: operation?.operationName,
          variables: JSON.stringify(operation?.variables),
          errors: JSON.stringify(graphQLErrors),
        };
        for (let err of graphQLErrors) {
          let operationName =
            errors?.name === "updateNotificationPrefrence" ? "updateNotification" : errors.name;

          // @ts-ignore
          apiLogEvents(analytics, "API", `${operationName}_${err?.code}`);
          // @ts-ignore
          switch (err.code) {
            case "401":
              if (err.message.includes("Access denied!")) {
                const oldHeaders = operation.getContext().headers;
                const userDetails = store.getState().userInfo;
                let token = userDetails?.userToken;
                const newHeaders = {
                  ...oldHeaders,
                  authorization: token ? `Bearer ${token}` : "",
                };
                const newOperation = {
                  ...operation,
                  getContext: () => ({
                    headers: newHeaders,
                  }),
                  setContext: () => ({
                    headers: newHeaders,
                  }),
                };

                return forward(newOperation);
              }
          }
        }
      }

      if (networkError) {
        console.log(`[Network error]: ${networkError}`);
      }
    }),
    responseLink.concat(
      perfLink.concat(
        requestResponseLink.concat(authLink.concat(createHttpLink({ uri: BACKEND_URI, fetch })))
      )
    ),
  ]),
  cache: new InMemoryCache(),
});
